import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(3),
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginRight: theme.spacing(2),
    },
    formCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
    },
    valueItem: {
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: "#fafafa",
    },
    valueItemHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
}));

const PlanFeatureEditingPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const { setLoading } = useLoading();
    
    const isEditMode = !!id && id !== "new";
    
    const [formData, setFormData] = useState({
        code: "",
        sequence: 0,
        type: "",
        titleEn: "",
        titleTc: "",
        values: [],
    });

    const [plans, setPlans] = useState([]);
    const [errors, setErrors] = useState({});

    // Load plans for dropdown
    useEffect(() => {
        loadPlans();
    }, []);

    // Load existing plan feature data if in edit mode
    useEffect(() => {
        if (isEditMode && id && id !== "new") {
            loadPlanFeatureData();
        }
    }, [id, isEditMode]);

    const loadPlans = async () => {
        try {
            setLoading(true);
            const response = await apiHelper.hkiff.getPlans({ keyword: null, page: 0, size: 1000 });
            if (response && response.data && response.data.content) {
                // Only include plans with status = 1 (active)
                const activePlans = response.data.content.filter(plan => plan.status === "ACTIVE");
                setPlans(activePlans);
            }
        } catch (error) {
            console.error("Error loading plans:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadPlanFeatureData = async () => {
        setLoading(true);
        try {
            const response = await apiHelper.hkiff.getPlanFeatureById(id);
            if (response && response.data) {
                const feature = response.data;
                setFormData({
                    code: feature.code || "",
                    sequence: feature.sequence !== undefined && feature.sequence !== null ? feature.sequence : 0,
                    type: feature.type || "",
                    titleEn: feature.titleEn || "",
                    titleTc: feature.titleTc || "",
                    values: feature.values && feature.values.length > 0 ? feature.values : [],
                });
            }
        } catch (error) {
            console.error("Error loading plan feature:", error);
            toast.error("Failed to load plan feature data.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleNumberChange = (field) => (event) => {
        const value = parseInt(event.target.value, 10) || 0;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleValueChange = (index, field) => (event) => {
        const value = event.target.value;
        setFormData(prev => {
            const newValues = [...prev.values];
            newValues[index] = {
                ...newValues[index],
                [field]: field === 'planId' ? parseInt(value, 10) : value
            };
            return {
                ...prev,
                values: newValues
            };
        });
    };

    const handleAddValue = () => {
        setFormData(prev => ({
            ...prev,
            values: [
                ...prev.values,
                {
                    planId: plans.length > 0 ? plans[0].id : null,
                    valueEn: "",
                    valueTc: ""
                }
            ]
        }));
    };

    const handleRemoveValue = (index) => {
        setFormData(prev => {
            const newValues = [...prev.values];
            newValues.splice(index, 1);
            return {
                ...prev,
                values: newValues
            };
        });
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.code.trim()) {
            newErrors.code = "Code is required";
        }
        
        if (!formData.type.trim()) {
            newErrors.type = "Type is required";
        }
        
        // if (!formData.titleEn.trim()) {
        //     newErrors.titleEn = "English title is required";
        // }
        
        // if (!formData.titleTc.trim()) {
        //     newErrors.titleTc = "Traditional Chinese title is required";
        // }

        // Validate values
        formData.values.forEach((value, index) => {
            if (!value.planId) {
                newErrors[`value_${index}_planId`] = "Plan is required";
            }
        });
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                code: formData.code.trim(),
                sequence: formData.sequence,
                type: formData.type.trim(),
                titleEn: formData.titleEn.trim() || " ",
                titleTc: formData.titleTc.trim() || " ",
                values: formData.values.map(value => ({
                    planId: value.planId,
                    valueEn: value.valueEn ? value.valueEn.trim() : "",
                    valueTc: value.valueTc ? value.valueTc.trim() : ""
                }))
            };

            let response;
            if (isEditMode) {
                response = await apiHelper.hkiff.updatePlanFeatureById(id, payload);
                if (response && response.data) {
                    toast.success("Plan feature updated successfully.");
                    history.push("/cineclub/membership/plan-feature");
                } else {
                    console.error("Failed to update plan feature:", response);
                    toast.error("Failed to update plan feature. Please try again.");
                }
            } else {
                response = await apiHelper.hkiff.createPlanFeature(payload);
                if (response && response.data) {
                    toast.success("Plan feature created successfully.");
                    history.push("/cineclub/membership/plan-feature");
                } else {
                    console.error("Failed to create plan feature:", response);
                    toast.error("Failed to create plan feature. Please try again.");
                }
            }
        } catch (error) {
            const action = isEditMode ? "updating" : "creating";
            console.error(`Error ${action} plan feature:`, error);
            toast.error(`An error occurred while ${action} the plan feature. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        history.push("/cineclub/membership/plan-feature");
    };

    return (
        <div className={classes.content}>
            <Box className={classes.header}>
                <Button
                    className={classes.backButton}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                >
                    Back
                </Button>
                <Typography variant="h4" component="h1">
                    {isEditMode ? "Edit Plan Feature" : "Create Plan Feature"}
                </Typography>
            </Box>

            <Paper className={classes.formCard}>
                <Typography variant="h6" gutterBottom>
                    Plan Feature Details
                </Typography>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Code"
                            variant="outlined"
                            value={formData.code}
                            onChange={handleInputChange("code")}
                            error={!!errors.code}
                            helperText={errors.code}
                            required
                            className={classes.formField}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl 
                            fullWidth 
                            variant="outlined" 
                            className={classes.formField}
                            error={!!errors.type}
                        >
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={formData.type}
                                onChange={handleInputChange("type")}
                                label="Type"
                            >
                                <MenuItem value="homepage">Homepage</MenuItem>
                                <MenuItem value="register">Register</MenuItem>
                                <MenuItem value="renewal">Renewal</MenuItem>
                            </Select>
                            {errors.type && (
                                <Typography variant="caption" color="error" style={{ marginTop: "8px", marginLeft: "14px" }}>
                                    {errors.type}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Title (English)"
                            variant="outlined"
                            value={formData.titleEn}
                            onChange={handleInputChange("titleEn")}
                            error={!!errors.titleEn}
                            helperText={errors.titleEn}
                            className={classes.formField}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Title (Traditional Chinese)"
                            variant="outlined"
                            value={formData.titleTc}
                            onChange={handleInputChange("titleTc")}
                            error={!!errors.titleTc}
                            helperText={errors.titleTc}
                            className={classes.formField}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                            <Typography variant="h6">
                                Plan Values
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddValue}
                                size="small"
                                // disabled={formData.values.length >= 3}
                            >
                                Add Value
                            </Button>
                        </Box>

                        {formData.values.length === 0 ? (
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: "16px" }}>
                                No values added. Click "Add Value" to add plan-specific values.
                            </Typography>
                        ) : (
                            formData.values.map((value, index) => (
                                <Box key={index} className={classes.valueItem}>
                                    <Box className={classes.valueItemHeader}>
                                        <Typography variant="subtitle2">
                                            Plan {index + 1}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveValue(index)}
                                            color="secondary"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <FormControl 
                                                fullWidth 
                                                variant="outlined"
                                                error={!!errors[`value_${index}_planId`]}
                                            >
                                                <InputLabel>Plan</InputLabel>
                                                <Select
                                                    value={value.planId || ""}
                                                    onChange={handleValueChange(index, "planId")}
                                                    label="Plan"
                                                >
                                                    {plans.map((plan) => (
                                                        <MenuItem key={plan.id} value={plan.id}>
                                                            {plan.localizations?.en?.name || plan.nameEn || plan.titleEn || plan.name || plan.title || `Plan ${plan.id}`}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors[`value_${index}_planId`] && (
                                                    <Typography variant="caption" color="error" style={{ marginTop: "8px", marginLeft: "14px" }}>
                                                        {errors[`value_${index}_planId`]}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Value (English)"
                                                variant="outlined"
                                                value={value.valueEn || ""}
                                                onChange={handleValueChange(index, "valueEn")}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Value (Traditional Chinese)"
                                                variant="outlined"
                                                value={value.valueTc || ""}
                                                onChange={handleValueChange(index, "valueTc")}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))
                        )}
                    </Grid>
                </Grid>

                <Box className={classes.buttonGroup}>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                    >
                        Save
                    </Button>
                </Box>
            </Paper>
        </div>
    );
};

export default PlanFeatureEditingPage;
